// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useGeneratedCourse.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useMutation, useQuery, UseMutationResult, UseQueryResult} from '@tanstack/react-query';

import {isSettled} from '../../Screens/LMS/courseStatus';
import {CourseJob, CourseList, CourseSummary} from './types';

/**
 * One row of the course list. `coach` is newer than `CourseSummary` and kept
 * here rather than in types.ts: "" when the server does not know, and absent
 * altogether from an API that predates it — so always fall back to `author`.
 * `archived_at` is set on rows of the Archived view. `is_public` is not sent
 * today; when it is, Delete knows a course is published before asking.
 */
export type CourseListRow = CourseSummary & {
  coach?: string;
  archived_at?: string | null;
  is_public?: boolean | null;
};
export type CourseListPayload = Omit<CourseList, 'courses'> & {courses: CourseListRow[]};

/** POST /archive/ 200. Each field may be an id list or a count. */
export type ArchiveResult = {
  archived?: number[] | number;
  not_found?: number[] | number;
  unpublished?: number[] | number;
  cancelled?: number[] | number;
};

/** POST /restore/ 200. */
export type RestoreResult = {
  restored?: number[] | number;
  not_found?: number[] | number;
};

/** Active courses (the default), or only the archived ones. */
export type CourseListView = 'active' | 'archived';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

export async function fetchCourseList(view: CourseListView = 'active'): Promise<CourseListPayload> {
  const response = await ChessRestClient.get('/video_learning/courses/', {
    headers: await authHeader(),
    ...(view === 'archived' ? {params: {archived: 1}} : {}),
  });
  return response.data as CourseListPayload;
}

/**
 * Every generated course, newest first. Unlike the course itself this carries
 * no presigned URLs, so it is safe to keep around and cheap to refetch.
 * The server leaves archived courses out unless the Archived view asks.
 */
export function useCourseList(
  view: CourseListView = 'active',
): UseQueryResult<CourseListPayload, Error> {
  return useQuery<CourseListPayload, Error>(
    ['generated-course-list', view],
    () => fetchCourseList(view),
    {staleTime: 60 * 1000},
  );
}

/**
 * Delete, which archives: hidden from the list and from students, and
 * restorable. Without `confirm` the server answers 409 `confirm_required` when
 * any course is published or still building; the screen asks the admin to type
 * DELETE and sends it again with it.
 */
export async function archiveCourses(
  ids: number[],
  confirm?: 'DELETE',
): Promise<ArchiveResult> {
  const response = await ChessRestClient.post(
    '/video_learning/courses/archive/',
    confirm ? {video_course_ids: ids, confirm} : {video_course_ids: ids},
    {headers: await authHeader()},
  );
  return response.data as ArchiveResult;
}

/** Brings archived courses back, as unpublished drafts. */
export async function restoreCourses(ids: number[]): Promise<RestoreResult> {
  const response = await ChessRestClient.post(
    '/video_learning/courses/restore/',
    {video_course_ids: ids},
    {headers: await authHeader()},
  );
  return response.data as RestoreResult;
}

export type CreateCoursePayload = {video_url?: string; video_course_id?: number; force?: boolean};

export async function createCourse(payload: CreateCoursePayload): Promise<CourseJob> {
  const response = await ChessRestClient.post(
    '/video_learning/courses/create/',
    payload,
    {headers: await authHeader()},
  );
  return response.data as CourseJob;
}

export function useCreateCourse(): UseMutationResult<CourseJob, Error, CreateCoursePayload> {
  return useMutation<CourseJob, Error, CreateCoursePayload>(createCourse);
}

export async function fetchCourseJob(videoCourseId: number): Promise<CourseJob> {
  const response = await ChessRestClient.get(
    `/video_learning/courses/${videoCourseId}/status/`,
    {headers: await authHeader()},
  );
  return response.data as CourseJob;
}

/**
 * Polls one generation while it runs.
 *
 * A real masterclass takes long enough that the screen has to be able to be
 * closed and reopened, so the job is keyed by the recording rather than held
 * in component state. Polling stops once the job reaches a terminal state —
 * otherwise a finished course would be refetched every few seconds forever.
 */
export function useCourseJob(
  videoCourseId: number | null,
  intervalMs = 5000,
): UseQueryResult<CourseJob, Error> {
  return useQuery<CourseJob, Error>(
    ['generated-course-job', videoCourseId],
    () => fetchCourseJob(videoCourseId as number),
    {
      enabled: videoCourseId != null,
      refetchInterval: data =>
        data && isSettled(data.status) ? false : intervalMs,
    },
  );
}
